/**
 * The Settings index — every settings page, as a grid of cards.
 *
 * **The page exists because the sidebar does not exist on a phone.** `AppShell`
 * renders the 236px nav `hidden … md:block` and the 64px rail
 * `hidden … sm:flex`, so under 768px the Settings sidebar is not on screen and
 * under 640px neither is the gear that switches to it. Every settings page was
 * reachable only by typing its URL.
 *
 * **It is one door to the domain, not three doors to three pages**, which is
 * what keeps it on the right side of ADR-0064. That ADR took Secure sign-in and
 * Your signature out of the account menu because two named links to one page is
 * two places to keep in agreement. A single link to the domain's own index adds
 * no second name for any page: this grid renders `settingsGroups()`, the same
 * function the sidebar renders, so the two cannot disagree about what exists or
 * about what it is called.
 */
export const SETTINGS_INDEX_COPY = {
  /**
   * The `<h1>`. Deliberately `APP_COPY.nav.settings`' word rather than a
   * sentence of its own — the rail's gear, the sidebar's wordmark, the account
   * menu's link and this heading are four ways into one domain, and a domain
   * with four names is a domain nobody can refer to.
   */
  title: 'Settings',
  /**
   * One line under the title. It says who the page is for rather than what is
   * on it, because what is on it varies by role and the cards say so
   * themselves.
   */
  summary: 'Everything you can change about your account, and about your agency.',
  /**
   * The header's back control on every settings page below `md` — `AppShell`
   * hides the sidebar there, so this page is the only way back to it without
   * the browser's own back button. `FOLDER_COPY.backToSpace`'s wording, one
   * domain over: "Back to X" names the destination rather than describing the
   * gesture.
   */
  backToSettings: 'Back to settings',
  /**
   * The line under each card's name.
   *
   * **Per entry, and every entry has one** — a grid of titles with empty second
   * lines reads as a page that failed to load. They are keyed by
   * `SettingsEntry.id`, which is `surfaces.ts`'s own key, so an entry added
   * there without a line here is a type error rather than a blank card.
   */
  details: {
    general: 'Light or dark, and anything else about this device.',
    passkeys: 'The passkeys that sign you in, and the devices they live on.',
    signature: 'The mark you place on a document you sign.',
    spaces: 'The rooms your agency shares, and who is in them.',
    users: 'Who at your agency has an account, and what they can reach.',
    storage: 'What your agency is holding, and what it has moved.',
    audit: 'Every act this system recorded, and who performed it.',
    // USR-16. Says what a scanner *is*, not only that there is a list — most
    // agencies will never have one, and the card is where they meet the idea.
    scanners: 'The file servers that upload into your spaces without signing in.',
  },
} as const;
